import { useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface PricingPlan {
  title: string;
  description: string;
  price: string;
  period: string;
  features: string[];
  restrictedFeatures?: string[];
  buttonText: string;
  buttonClassName?: string;
  checkColor?: string;
  isBestValue?: boolean;
  onSubscribe: () => void;
}

interface MobilePricingTableProps {
  plans: PricingPlan[];
}

export const MobilePricingTable = ({ plans }: MobilePricingTableProps) => {
  const [selectedPlan, setSelectedPlan] = useState<number>(1); // Default to middle plan

  const handlePlanSelect = (index: number) => {
    setSelectedPlan(index);
  };

  const currentPlan = plans[selectedPlan];

  return (
    <div className="w-full space-y-6">
      {/* Plan Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {plans.map((plan, index) => (
          <button
            key={plan.title}
            onClick={() => handlePlanSelect(index)}
            className={cn(
              "p-6 rounded-xl text-left transition-all border-2",
              selectedPlan === index 
                ? "border-amber-500 bg-amber-50/50" 
                : "border-gray-200 hover:border-gray-300",
              plan.isBestValue && "relative"
            )}
          >
            {plan.isBestValue && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                Melhor Valor
              </div>
            )}
            <div className="space-y-2">
              <h3 className="text-xl font-bold">{plan.title}</h3>
              <p className="text-sm text-gray-600 min-h-[40px]">{plan.description}</p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold">{plan.price}</span>
                <span className="text-gray-600">{plan.period}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Features Table */}
      <div className="mt-8 border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Funcionalidades</TableHead>
              <TableHead className="text-center">Disponível</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPlan.features.map((feature, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{feature}</TableCell>
                <TableCell className="text-center">
                  <Check className={cn("h-5 w-5 mx-auto", currentPlan.checkColor || "text-gray-500")} />
                </TableCell>
              </TableRow>
            ))}
            {currentPlan.restrictedFeatures?.map((feature, index) => (
              <TableRow key={`restricted-${index}`} className="opacity-50">
                <TableCell className="font-medium">{feature}</TableCell>
                <TableCell className="text-center">
                  <X className="h-5 w-5 mx-auto text-red-500" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Action Button */}
      <div className="mt-8">
        <Button 
          onClick={currentPlan.onSubscribe}
          className={cn(
            "w-full py-6 text-lg",
            currentPlan.buttonClassName
          )}
        >
          {currentPlan.buttonText}
        </Button>
      </div>
    </div>
  );
};